import { useMemo, useState, useEffect, useRef } from 'react'
import {
  useSetToast,
  useNavigate,
  useResetOrderAndCaseAtoms,
  importCaseCSV,
} from 'hooks'
import { orderAtom, caseAtom, orderIdAtom } from 'atoms'
import { useSetRecoilState } from 'recoil'
import {
  ArchiveFooter,
  FullscreenModal,
  MoreActionsButton,
  Table,
  CaseDetails,
  CasesTableLite,
  ImportFailureModal,
  UserDropDown,
} from 'components'
import LanguageSelector from 'components/global/LanguageSelector/LanguageSelector'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import * as S from './DashboardTable.styled'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useTranslation } from 'react-i18next'
import { LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE } from '@messenger/consts'
import { getOrderProducts } from '@/api/orderProducts'
import { createCase, getCase } from '@/api/cases'
import { useCaseImageVariants } from '@/hooks/caseImages'
import { EventCategory, logger } from 'utils/logger'
import {
  createOrder as createOrderApi,
  duplicateOrder as duplicateOrderApi,
  getOrder,
} from '@/api/orders'

export const DashboardTable = ({
  type,
  title,
  api,
  mockTable,
  TableComponent,
  CreateComponent,
}) => {
  const { t, i18n } = useTranslation()
  const handleError = useErrorHandler('DashboardTable')
  const setToast = useSetToast()
  const navigate = useNavigate()
  const [tableContent, setTableContent] = useState(mockTable)
  const [itemsLoaded, setItemsLoaded] = useState(false)
  const [showingArchived, setShowingArchived] = useState(false)
  const [showCheckboxes, setShowCheckboxes] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [updateTable, setUpdateTable] = useState(Date.now())
  const [openCaseModal, setOpenCaseModal] = useState(false)
  const [duplicateOrderId, setDuplicateOrderId] = useState(null)
  const [showCaseDetailsModal, setShowCaseDetailsModal] = useState(false)
  const setCase = useSetRecoilState(caseAtom)
  const { loadCaseImages } = useCaseImageVariants()
  const [csvFailed, setCsvFailed] = useState(false)
  const [refreshCases, setRefresh] = useState(false)
  const setOrder = useSetRecoilState(orderAtom)
  const setOrderId = useSetRecoilState(orderIdAtom)
  const resetAtoms = useResetOrderAndCaseAtoms()
  const inputFile = useRef(null)

  const getItems = async (archived, skipCaseReset) => {
    // retreives the items to populate the table
    const result = await api?.[`getFuneralHome${type}s`](archived)
    const { data } = result || {}
    if (tableContent) {
      setTableContent(data[`${type.toLowerCase()}s`])
      setItemsLoaded(true)
    }

    resetAtoms(skipCaseReset)
  }

  const archive = async (multiple, item) => {
    // determine which items will be mapped through, and what will be archived
    const items = multiple ? selectedRows : [item]
    const archiveFunction = showingArchived
      ? `unarchive${type}`
      : `archive${type}`

    try {
      // archives or unarchives the selected items
      await Promise.all(
        items.map(async (item) => {
          await api?.[archiveFunction](item?.original?.id)
        }),
      )

      // retrieves the new items and hides the checkboxes
      setUpdateTable(Date.now())

      // displays success toast
      setToast({
        text: showingArchived
          ? t('toast.showingArchived', { type })
          : t('toast.showingUnarchived', { type }),
        icon: 'check',
      })

      setShowCheckboxes(false)
    } catch {
      // displays failure toast
      setToast({ text: t('toast.serverError') })
    }
  }

  const toggleArchived = async (archived) => {
    // toggles between unarchived and archived
    setItemsLoaded(false)
    setShowingArchived(archived)
    await getItems(archived)
  }

  const clearSelectedRows = async () => {
    // unselects all checked rows
    setSelectedRows([])
    setShowCheckboxes(false)
  }

  const goToOrder = async (id) => {
    try {
      // Reset atoms when switching to a different order
      resetAtoms(true) // Skip case reset since we're just switching orders
      setOrderId(id)
      const { data: orderData } = await getOrder(id)
      setOrder(orderData)

      // Check if order products have been fetched for this order
      // This is a simplified check; ideally, we'd know if products were *selected*
      let orderProductsData = { data: [] } // Default to empty if not fetched
      try {
        // Attempt to get order products. This indicates if we *can* skip the product page.
        orderProductsData = await getOrderProducts(orderData.id)
      } catch (e) {
        // If this fails, it's okay, means no products selected yet or an issue fetching.
        // We'll default to the products page.
        console.warn(
          'Could not fetch order products for navigation decision:',
          e,
        )
      }

      if (orderData.theme_id) {
        navigate(`/package/${orderData.id}/customize`)
      } else if (orderProductsData?.data?.length > 0) {
        navigate(`/package/${orderData.id}/theme`)
      } else {
        navigate(`/package/${orderData.id}/products`) // Default to products page
      }
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'goToOrder',
      )
    }
  }

  const createOrder = async (id) => {
    try {
      resetAtoms()
      const { data } = await createOrderApi({ case_id: id })
      setOrder(data)
      setOrderId(data.id)

      // Directly navigate to the products page for the new order
      navigate(`/package/${data.id}/products`)
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'createOrder',
      )
    }
  }

  const openDuplicateOrder = (orderId) => {
    setOpenCaseModal(true)
    setDuplicateOrderId(orderId)
  }

  const duplicateOrder = async (caseId) => {
    const payload = {
      order_id: duplicateOrderId,
      case_id: caseId,
    }
    try {
      const { data } = await duplicateOrderApi(payload)
      if (data?.id) {
        setToast({
          text: t('toast.orderDuplicated'),
          icon: 'check',
        })
      }
    } catch (err) {
      // An order holding the new text boxes is not copied to a funeral home
      // they are not turned on for, and nothing was created.
      const lotusRefused =
        err?.response?.data?.code === LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE
      setToast({
        text: lotusRefused
          ? t('toast.orderDuplicateLotusRefused')
          : t('toast.somethingWentWrong', {
              message: err?.response?.data?.message || err.message,
            }),
      })
    }
    setDuplicateOrderId(null)
    setOpenCaseModal(false)
    await getItems(false)
  }

  const openCaseDetailsModal = async (caseId) => {
    try {
      const { data: _case } = await getCase(caseId)
      setCase(_case)
      try {
        // Only the image list is awaited; the case's AI variants and the
        // variant pricing are requested alongside and land when they answer.
        // A failure empties the list unless a newer case has been opened since.
        await loadCaseImages(caseId, { clearOnFailure: true })
      } catch (err) {
        // The logger keeps only the error's name, message and stack, never
        // the request config, whose headers carry the session's bearer token.
        logger.warn({
          message: 'case_images_load_failed',
          category: EventCategory.ERROR,
          error: err instanceof Error ? err : undefined,
          data: { caseId, httpStatus: err?.response?.status },
        })
      }
      setShowCaseDetailsModal(true)
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'openCaseDetailsModal',
      )
    }
  }

  const handleFileChange = async (e) => {
    const { files } = e.target
    if (files && files.length) {
      const result = await importCaseCSV(files[0])
      try {
        await createCase(result)
        setToast({
          text: t('toast.caseImported'),
          icon: 'check',
        })
      } catch (err) {
        handleError(
          err?.response?.data?.message || err.message,
          err,
          null,
          'handleFileChange',
        )
        setCsvFailed(true)
      }
      inputFile.current.value = ''
      getItems(false)
      setRefresh(true)
    }
  }

  const openFile = () => {
    inputFile.current.click()
  }

  useEffect(() => {
    getItems(showingArchived)
    // eslint-disable-next-line
  }, [updateTable])

  const goToTableRow = (id) => {
    if (type === 'Order') {
      goToOrder(id)
    } else if (type === 'Case') {
      openCaseDetailsModal(id)
    }
  }

  const columns = useMemo(
    () =>
      TableComponent({
        t,
        language: i18n.language,
        itemsLoaded: itemsLoaded,
        showingArchived: showingArchived,
        archive: archive,
        openCaseDetailsModal: openCaseDetailsModal,
        editPackage: goToOrder,
        openDuplicateOrder: openDuplicateOrder,
      }),
    // Excluding TableComponent and functions to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, itemsLoaded, showingArchived, showCheckboxes],
  )

  const initialSortBy = useMemo(() => {
    if (type === 'Order') {
      return [{ id: 'case.name_of_deceased', desc: false }] // Sort by Deceased Name, ascending
    }
    return [] // No initial sort for other types or default
  }, [type])

  return (
    <Flex column align="center">
      <ImportFailureModal
        show={[csvFailed, setCsvFailed]}
        openFile={openFile}
      />

      <Flex
        align="center"
        background="paleSecondary"
        full
        height="94px"
        justify="space-between"
        padding="0 42px"
      >
        <Flex gap="24px">
          <Text
            size="32px"
            weight="600"
            data-testid="dashboard-print-packages-header"
          >
            {title}
          </Text>
        </Flex>

        <Flex align="center">
          <MoreActionsButton
            showingArchived={showingArchived}
            toggleCheckboxes={() => setShowCheckboxes(!showCheckboxes)}
            openFile={openFile}
          />
          <CreateComponent
            buttonText={t('dashboard.newPrintPackage')}
            rowClick={createOrder}
            type={type}
            importCase={openFile}
            refresh={[refreshCases, setRefresh]}
            getItems={getItems}
          />
          <UserDropDown />
          <LanguageSelector />
        </Flex>
      </Flex>
      <S.HiddenInput ref={inputFile} onChange={handleFileChange} type="file" />
      <Flex padding="32px 42px 0 42px" full>
        <Table
          columns={columns}
          data={tableContent}
          setItemsLoaded={setItemsLoaded}
          setSelectedRows={setSelectedRows}
          searchPlaceholder={t('search.searchPlaceholder')}
          showingArchived={showingArchived}
          showCheckboxes={showCheckboxes}
          toggleArchived={toggleArchived}
          defaultClick={({ original: { id } }) => goToTableRow(id)}
          initialSortBy={initialSortBy}
        />
      </Flex>

      <FullscreenModal show={[openCaseModal, setOpenCaseModal]}>
        <CasesTableLite
          rowClick={duplicateOrder}
          setShowModal={setOpenCaseModal}
          height="100vh"
          type="duplicate"
        />
      </FullscreenModal>

      <FullscreenModal show={[showCaseDetailsModal, setShowCaseDetailsModal]}>
        {showCaseDetailsModal && <CaseDetails updateTable={getItems} />}
      </FullscreenModal>

      <ArchiveFooter
        archive={archive}
        clearSelectedRows={clearSelectedRows}
        selectedRows={selectedRows}
        show={showCheckboxes}
        showingArchived={showingArchived}
      />
    </Flex>
  )
}

export default DashboardTable
